import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { QRCodeCanvas } from 'qrcode.react';
import { useSnackbar } from 'notistack';

import Iconify from 'src/components/iconify';
import { fetchOrganizationInviteLink } from 'src/api/invite-link';

/**
 * The organization's volunteer invite link, as a QR code to hold up or print and a URL to send.
 *
 * The QR and the two buttons are the interface; the URL sits in a read-only field to be copied,
 * not transcribed. The token is base64url, so it is case-sensitive and contains l/I/1 and O/0 --
 * fine to scan or tap, unkind to read down a phone line.
 */
export default function InviteVolunteersDialog({ open, onClose, organizationId }) {
  const { enqueueSnackbar } = useSnackbar();
  const qrRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !organizationId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');
    fetchOrganizationInviteLink(organizationId)
      .then((data) => {
        if (cancelled) return;
        setLink(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.data?.message || e?.message || 'Could not load the invite link.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, organizationId]);

  const onCopy = useCallback(async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      enqueueSnackbar('Link copied');
    } catch {
      enqueueSnackbar('Could not copy the link', { variant: 'error' });
    }
  }, [link, enqueueSnackbar]);

  const onDownload = useCallback(() => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const anchor = document.createElement('a');
    anchor.href = canvas.toDataURL('image/png');
    anchor.download = `${(link?.organizationName || 'organization')
      .replace(/[^a-z0-9]+/gi, '-')
      .toLowerCase()}-volunteer-invite.png`;
    anchor.click();
  }, [link]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Invite volunteers</DialogTitle>

      <DialogContent>
        {!organizationId && (
          <Alert severity="info">Choose an organization to get its invite link.</Alert>
        )}

        {!!organizationId && loading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        )}

        {!!organizationId && !loading && !!error && <Alert severity="error">{error}</Alert>}

        {!!organizationId && !loading && !error && !!link?.url && (
          <Stack spacing={2.5} alignItems="center">
            <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Anyone who opens this link can sign up as a volunteer for{' '}
              <strong>{link.organizationName}</strong>. They will appear in this list straight
              away.
            </Typography>

            <div ref={qrRef}>
              <QRCodeCanvas value={link.url} size={220} includeMargin />
            </div>

            <TextField
              value={link.url}
              label="Invite link"
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              onFocus={(event) => event.target.select()}
            />

            <Stack direction="row" spacing={1.5} sx={{ width: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={onCopy}
                startIcon={<Iconify icon="solar:copy-bold" />}
              >
                Copy link
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                onClick={onDownload}
                startIcon={<Iconify icon="solar:download-minimalistic-bold" />}
              >
                Download QR
              </Button>
            </Stack>

            <Alert severity="warning" sx={{ width: 1 }}>
              This link does not expire.
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
